import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Plus, Clock, Users, Video } from 'lucide-react'
import { Button } from './ui/button'
import { Card } from './ui/card'
import { cn } from '@/lib/utils'
import { createClient } from '@blinkdotnew/sdk'
import type { CalendarEvent as CalendarEventType } from '@/types/calendar'
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
})

interface CalendarEvent {
  id: string
  title: string
  start: Date
  end: Date
  type: 'meeting' | 'call' | 'event' | 'reminder' | 'task'
  attendees?: number
  color?: string
}

export function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState<'week' | 'day'>('week')
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)

  // Load events from database + real-time subscription
  useEffect(() => {
    let unsubscribeRealtime: (() => void) | null = null

    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      if (state.user) {
        loadEvents(state.user.id)

        // Subscribe to real-time calendar updates
        try {
          unsubscribeRealtime = await blink.realtime.subscribe(
            `calendar-${state.user.id}`,
            (message) => {
              if (message.type === 'new-event' || message.type === 'event-updated') {
                console.log('Real-time calendar update, reloading...')
                loadEvents(state.user.id)
              }
            }
          )
        } catch (error) {
          console.log('Real-time calendar unavailable')
        }
      } else {
        setEvents([])
        setLoading(false)
      }
    })

    return () => {
      unsubscribe()
      if (unsubscribeRealtime) {
        unsubscribeRealtime()
      }
    }
  }, [])

  const loadEvents = async (userId: string) => {
    try {
      setLoading(true)

      // Load from calendar_events_enhanced table
      console.log('📅 [Calendar Widget] Loading events for user:', userId)
      const dbEvents = await blink.db.calendarEventsEnhanced.list({
        where: { userId },
        orderBy: { startTime: 'asc' },
        limit: 1000
      })
      console.log('📅 [Calendar Widget] Loaded:', dbEvents.length, 'events')

      const transformedEvents: CalendarEvent[] = dbEvents.map((e: any) => {
        let attendeesCount = 0
        try {
          if (e.attendees) {
            const parsed = typeof e.attendees === 'string' ? safeJsonParse(e.attendees) : e.attendees
            attendeesCount = Array.isArray(parsed) ? parsed.length : 0
          }
        } catch {
          attendeesCount = 0
        }

        // Use startTime/endTime or fallback to startDate/endDate
        const startTime = e.startTime || e.startDate
        const endTime = e.endTime || e.endDate

        return {
          id: e.id,
          title: e.title || '(No Title)',
          start: new Date(startTime),
          end: new Date(endTime),
          type: (e.eventType || 'event') as any,
          attendees: attendeesCount,
          color: getDefaultColor(e.eventType || 'event')
        }
      })

      console.log('📅 [Calendar Widget] Transformed:', transformedEvents.length, 'events')
      setEvents(transformedEvents)
    } catch (error) {
      console.error('❌ [Calendar Widget] Error loading calendar events:', error)
    } finally {
      setLoading(false)
    }
  }

  const getDefaultColor = (type: string) => {
    switch (type) {
      case 'meeting': return 'from-blue-500/20 to-cyan-500/20'
      case 'reminder': return 'from-amber-500/20 to-orange-500/20'
      case 'task': return 'from-green-500/20 to-emerald-500/20'
      default: return 'from-purple-500/20 to-pink-500/20'
    }
  }

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  
  const getWeekDays = () => {
    const days = []
    const startOfWeek = new Date(currentDate)
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1)
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek)
      day.setDate(startOfWeek.getDate() + i)
      days.push(day)
    }
    return days
  }

  const weekDays = getWeekDays()

  const getEventsForDay = (day: Date) => {
    return events.filter(event => 
      event.start.getDate() === day.getDate() &&
      event.start.getMonth() === day.getMonth() &&
      event.start.getFullYear() === day.getFullYear()
    )
  }

  const getEventStyle = (event: CalendarEvent) => {
    const startHour = event.start.getHours() + event.start.getMinutes() / 60
    const duration = (event.end.getTime() - event.start.getTime()) / (1000 * 60 * 60)
    
    return {
      top: `${(startHour / 24) * 100}%`,
      height: `${(duration / 24) * 100}%`
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newDate = new Date(currentDate)
                  newDate.setDate(currentDate.getDate() - 7)
                  setCurrentDate(newDate)
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-xl font-bold">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newDate = new Date(currentDate)
                  newDate.setDate(currentDate.getDate() + 7)
                  setCurrentDate(newDate)
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentDate(new Date())}
            >
              Today
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 glass rounded-lg p-1">
              <Button
                variant={view === 'day' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('day')}
                className="text-xs"
              >
                Day
              </Button>
              <Button
                variant={view === 'week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('week')}
                className="text-xs"
              >
                Week
              </Button>
            </div>
            <Button size="sm" className="gap-2">
              <Plus className="w-4 h-4" />
              New Event
            </Button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-8 gap-2 mb-2">
          <div className="text-xs text-muted-foreground"></div>
          {weekDays.map((day, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={cn(
                'glass rounded-lg p-3 text-center',
                day.getDate() === new Date().getDate() && 'ring-2 ring-primary'
              )}
            >
              <div className="text-xs text-muted-foreground mb-1">{daysOfWeek[i]}</div>
              <div className="text-lg font-bold">{day.getDate()}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      <div className="glass-card rounded-xl p-4 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-semibold mb-2">No events scheduled</p>
            <p className="text-sm">Create your first event to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-8 gap-2 relative">
          {/* Time column */}
          <div className="space-y-[calc(3rem-2px)]">
            {hours.map(hour => (
              <div key={hour} className="text-xs text-muted-foreground text-right pr-2 h-12">
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, dayIndex) => (
            <div key={dayIndex} className="relative">
              {/* Hour lines */}
              {hours.map(hour => (
                <div
                  key={hour}
                  className="h-12 border-t border-border/20"
                />
              ))}

              {/* Events */}
              <div className="absolute inset-0">
                {getEventsForDay(day).map(event => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ scale: 1.02, zIndex: 10 }}
                    style={getEventStyle(event)}
                    className={cn(
                      'absolute left-0 right-0 mx-1 p-2 rounded-lg',
                      'glass-card cursor-pointer transition-all',
                      'hover:shadow-lg hover:ring-2 hover:ring-primary/50',
                      'bg-gradient-to-br',
                      event.color || 'from-primary/20 to-accent/20'
                    )}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-xs font-semibold truncate">{event.title}</p>
                      {event.type === 'video' ? (
                        <Video className="w-3 h-3 text-primary flex-shrink-0 ml-1" />
                      ) : (
                        <Clock className="w-3 h-3 text-primary flex-shrink-0 ml-1" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {event.start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {event.attendees && (
                      <div className="flex items-center gap-1 mt-1">
                        <Users className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{event.attendees}</span>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Today\'s Events', value: '3', icon: Clock, color: 'from-blue-500/20 to-cyan-500/20' },
          { label: 'This Week', value: '12', icon: Users, color: 'from-purple-500/20 to-pink-500/20' },
          { label: 'Video Calls', value: '4', icon: Video, color: 'from-green-500/20 to-emerald-500/20' },
          { label: 'Attendees', value: '29', icon: Users, color: 'from-amber-500/20 to-orange-500/20' }
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className={cn('glass-card rounded-xl p-4 bg-gradient-to-br', stat.color)}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 glass rounded-lg">
                <stat.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}
